import Image from "@tiptap/extension-image"

// Recognised size presets. Mirror Pages::MarkdownRenderer::IMAGE_SIZE_TITLE_RE
// in markdown_renderer.rb — both sides must agree on the vocabulary.
export const IMAGE_SIZE_RE = /^size-(sm|md|lg|full)$/
const VALID_SIZES = new Set(["sm", "md", "lg", "full"])

// Extends @tiptap/extension-image with a `size` attribute (sm/md/lg/full).
// Size round-trips through the markdown image title (`![alt](url "size-md")`)
// so the source stays valid CommonMark; the server-side renderer rewrites
// matching titles into `class="img-size-X"`.
export const ImageWithSize = Image.extend({
  addAttributes() {
    const parentAttrs = this.parent?.() || {}
    return {
      ...parentAttrs,
      size: {
        default: null,
        parseHTML: (el) => {
          const ds = el.getAttribute("data-size")
          if (ds && VALID_SIZES.has(ds)) return ds
          const m = el.getAttribute("title")?.match(IMAGE_SIZE_RE)
          return m ? m[1] : null
        },
        renderHTML: (attrs) => {
          if (!attrs.size) return {}
          return {
            "data-size": attrs.size,
            class: `img-size-${attrs.size}`
          }
        }
      },
      // Suppress the title attribute when it's our size marker so the
      // editor doesn't show "size-md" as a hover tooltip.
      title: {
        ...parentAttrs.title,
        parseHTML: (el) => {
          const t = el.getAttribute("title")
          return t && IMAGE_SIZE_RE.test(t) ? null : t
        }
      }
    }
  },

  addCommands() {
    return {
      ...this.parent?.(),
      // size: null | "sm" | "md" | "lg" | "full"
      // Uses Tiptap's `updateAttributes` so we work regardless of whether
      // the image is a NodeSelection (user clicked it) or just the
      // surrounding active node (cursor adjacent after a prior update).
      // Reading state.selection directly broke on the second click in a
      // row because the chain's `focus()` step can collapse a
      // NodeSelection.
      setImageSize: (size) => ({ commands }) => {
        if (size !== null && !VALID_SIZES.has(size)) return false
        return commands.updateAttributes(this.name, { size })
      }
    }
  },

  // Override the markdown serializer so `size` becomes the markdown title.
  // The default tiptap-markdown image serializer reads node.attrs.title, so
  // we replicate its shape but substitute the size marker when set.
  addStorage() {
    return {
      ...this.parent?.(),
      markdown: {
        ...(this.parent?.()?.markdown || {}),
        serialize(state, node) {
          const alt = state.esc(node.attrs.alt || "")
          const src = state.esc(node.attrs.src)
          const titleText = node.attrs.size ? `size-${node.attrs.size}` : node.attrs.title
          const titlePart = titleText ? ` ${state.quote(titleText)}` : ""
          state.write(`![${alt}](${src}${titlePart})`)
        }
      }
    }
  }
})
